import './DigitRoller.css';
import { useRef } from 'preact/hooks';

/** A05 (§3.6.3): o j-ésimo caractere que mudou, contado a partir da direita, começa em 80 + 35·j ms. */
export const ROLL_BASE_DELAY_MS = 80;
export const ROLL_STEP_MS = 35;

export interface DigitRollerProps {
  text: string;
  /** false = troca seca (replay, Countdown de §10.8). Movimento reduzido já troca seco pelo CSS. Padrão true. */
  animate?: boolean;
  /** Classes extras do dono (tipografia). */
  class?: string;
}

interface Cell {
  ch: string;
  /** Caractere que sai por cima (só nas células que rolam nesta troca). */
  old: string | null;
  rolling: boolean;
  /** Geração da última troca desta célula: muda a chave dos filhos e reinicia a animação. */
  gen: number;
  delay: number;
}

interface RollState {
  text: string;
  gen: number;
  cells: Cell[];
}

const chars = (s: string): string[] => Array.from(s);

function initial(text: string): RollState {
  return { text, gen: 0, cells: chars(text).map((ch) => ({ ch, old: null, rolling: false, gen: 0, delay: 0 })) };
}

/** Compara alinhando pela direita (números crescem para a esquerda); só as células que mudaram rolam. */
function roll(prev: RollState, text: string, animate: boolean): RollState {
  const gen = prev.gen + 1;
  const next = chars(text);
  const cells: Cell[] = new Array(next.length);
  let j = 0;
  for (let r = 0; r < next.length; r++) {
    const i = next.length - 1 - r;
    const ch = next[i] as string;
    const before = prev.cells[prev.cells.length - 1 - r];
    if (before && before.ch === ch) {
      cells[i] = { ch, old: null, rolling: false, gen: before.gen, delay: 0 };
      continue;
    }
    cells[i] = animate
      ? { ch, old: before?.ch ?? null, rolling: true, gen, delay: ROLL_BASE_DELAY_MS + ROLL_STEP_MS * j }
      : { ch, old: null, rolling: false, gen, delay: 0 };
    j++;
  }
  return { text, gen, cells };
}

/**
 * Dígitos que rolam (§3.8, A05): ao mudar, o caractere novo entra de baixo e o velho sai por cima, 420 ms
 * --ease-digit, com atraso 80 + 35·j ms só nos que mudaram (AC-3.8-30). Texto final em .sr-only; parte animada
 * aria-hidden (§15.4.3).
 */
export function DigitRoller({ text, animate = true, class: extra }: DigitRollerProps) {
  const state = useRef<RollState | null>(null);
  if (state.current === null) state.current = initial(text);
  else if (state.current.text !== text) state.current = roll(state.current, text, animate);
  const { cells } = state.current;
  const n = cells.length;
  return (
    <span class={extra ? `digit-roller ${extra}` : 'digit-roller'} data-testid="digit-roller">
      <span class="digit-roller__chars" aria-hidden="true">
        {cells.map((c, i) => (
          <span
            key={n - 1 - i}
            class="digit-roller__cell"
            data-roll={c.rolling ? '' : undefined}
            style={c.rolling ? { '--dur-digit-delay': `${c.delay}ms` } : undefined}
          >
            <span key={`n${c.gen}`} class="digit-roller__new">
              {c.ch}
            </span>
            {c.rolling && c.old !== null ? (
              <span key={`o${c.gen}`} class="digit-roller__old">
                {c.old}
              </span>
            ) : null}
          </span>
        ))}
      </span>
      <span class="sr-only">{text}</span>
    </span>
  );
}
